(() => {
  'use strict';

  const WORLD_EVENTS = new Set([
    'portal_click',
    'quick_choice_click',
    'product_click',
    'compare_click',
    'editorial_click',
    'cross_route_click'
  ]);
  const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
  const finePointer = matchMedia('(hover: hover) and (pointer: fine)');
  const observers = new Map();
  const settleTimers = new WeakMap();

  const deviceType = () => innerWidth < 750 ? 'mobile' : innerWidth < 1100 ? 'tablet' : 'desktop';
  const v112CustomAllowed = () => document.body?.dataset.avV112 === 'true'
    && document.body.dataset.avCustomEvents === 'true'
    && window.Shopify?.designMode !== true
    && window.AVAnalytics?.consentAllowed?.() === true
    && typeof window.AVAnalytics?.custom === 'function';
  const closestWorld = (node) => node?.closest?.('[data-category-world],[data-au-world],[data-av-world-root]');
  const analytics = () => window.AVAnalytics?.custom;
  const payload = (node, overrides = {}) => {
    const scope = closestWorld(node) || document.body;
    const module = node?.closest?.('[data-av-world-module]') || scope;
    return {
      world: overrides.world || node?.dataset?.avWorld || scope?.dataset?.auWorld || scope?.dataset?.categoryWorld || 'neutral',
      modifier: scope?.dataset?.categoryModifier || scope?.dataset?.benefitWorld || '',
      occasion: scope?.dataset?.auDayMoment || '',
      page_type: document.body?.dataset?.pageType || '',
      template: document.body?.dataset?.templateSuffix || document.body?.dataset?.template || '',
      section_id: module?.dataset?.avSectionId || '',
      module_type: module?.dataset?.avWorldModule || '',
      position: node?.closest?.('[data-av-position]')?.dataset?.avPosition || '',
      product_handle: node?.closest?.('[data-av-product-handle]')?.dataset?.avProductHandle || '',
      collection_handle: node?.closest?.('[data-collection-handle]')?.dataset?.collectionHandle || '',
      result_count: node?.dataset?.avResultCount || scope?.dataset?.avResultCount || '',
      device_type: deviceType(),
      destination: node?.closest?.('a')?.getAttribute('href') || '',
      label: node?.dataset?.avLabel || node?.querySelector?.('strong')?.textContent || node?.textContent || '',
      ...overrides
    };
  };

  const emit = (eventName, node, overrides = {}) => {
    if (!analytics()) return false;
    const eventPayload = payload(node, overrides);
    return window.AVAnalytics.custom(eventName, eventPayload, {
      dedupeKey: `${eventName}:${location.pathname}:${eventPayload.section_id}:${eventPayload.position}`,
      ttl: eventName === 'category_world_view' || eventName === 'au_world_view' ? 30000 : 800
    });
  };

  const emitV112 = (eventName, node, overrides = {}, ttl = 30000) => {
    if (!v112CustomAllowed()) return false;
    const eventPayload = payload(node, {
      source: 'homepage_world_sequence',
      variant: closestWorld(node)?.dataset?.avVariant || '',
      ...overrides
    });
    return window.AVAnalytics.custom(eventName, eventPayload, {
      dedupeKey: [eventName, location.pathname, eventPayload.section_id, eventPayload.world, eventPayload.position, eventPayload.destination].join(':'),
      ttl
    });
  };

  const activateWorld = (node) => {
    const chapter = node?.closest?.('[data-au-day-chapter]');
    const journey = chapter?.closest?.('[data-av-v112-world-sequence]');
    if (!chapter || !journey) return;
    const world = chapter.dataset.auWorld || chapter.dataset.categoryWorld || 'neutral';
    journey.dataset.auActiveWorld = world;
    journey.querySelectorAll('[data-au-day-chapter]').forEach((card) => {
      card.dataset.auWorldActive = String(card === chapter);
    });
  };

  const keepCollectionChipVisible = (node) => {
    const link = node?.closest?.('.au-collection-hero--cinco-fomes .au-collection-hero__links .au-collection-hero__link');
    if (!link) return;
    link.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'auto' });
  };

  const emitWorldEnter = (node) => {
    if (node?.dataset?.avV112WorldEntered === 'true') return;
    if (emitV112('australia:world_enter', node)) node.dataset.avV112WorldEntered = 'true';
  };

  const reveal = (node) => {
    node.dataset.auWorldRevealed = 'true';
    activateWorld(node);
    if (!reducedMotion.matches) {
      node.dataset.auTrackActive = 'true';
      clearTimeout(settleTimers.get(node));
      settleTimers.set(node, setTimeout(() => {
        node.dataset.auTrackActive = 'settled';
        settleTimers.delete(node);
      }, 1200));
    }
  };

  const observeViews = (root = document) => {
    const nodes = Array.from(root.querySelectorAll?.('[data-av-world-view]:not([data-av-world-observed])') || []);
    if (!nodes.length) return;
    if (!('IntersectionObserver' in window)) {
      nodes.forEach((node) => {
        node.dataset.avWorldObserved = 'true';
        reveal(node);
        emit('category_world_view', node);
        if ((node.dataset.auWorld || node.dataset.categoryWorld || 'neutral') !== 'neutral') emit('au_world_view', node);
        emitWorldEnter(node);
      });
      return;
    }
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.dataset.avWorldObserved = 'true';
        reveal(entry.target);
        emit('category_world_view', entry.target);
        if ((entry.target.dataset.auWorld || entry.target.dataset.categoryWorld || 'neutral') !== 'neutral') emit('au_world_view', entry.target);
        emitWorldEnter(entry.target);
        observer.unobserve(entry.target);
      });
    }, { threshold: .28, rootMargin: '0px 0px -8% 0px' });
    nodes.forEach((node) => observer.observe(node));
    observers.set(root, observer);
  };

  const init = (root = document) => {
    document.documentElement.dataset.avCategoryWorldReady = 'true';
    document.documentElement.dataset.avCategoryWorldMotion = reducedMotion.matches ? 'reduced' : 'enabled';
    observeViews(root);
  };

  const teardown = (root) => {
    observers.get(root)?.disconnect();
    observers.delete(root);
    root.querySelectorAll?.('[data-au-day-chapter]').forEach((node) => {
      clearTimeout(settleTimers.get(node));
      settleTimers.delete(node);
    });
  };

  document.addEventListener('click', (event) => {
    const target = event.target.closest?.('[data-av-world-action], [data-av-world-module] a, [data-av-world-module] button');
    if (!target) return;
    const moduleType = target.closest?.('[data-av-world-module]')?.dataset?.avWorldModule || '';
    const inferredEvent = {
      portal: 'portal_click',
      quick_choice: 'quick_choice_click',
      product_rail: 'product_click',
      sensory_comparison: 'compare_click',
      editorial_break: 'editorial_click',
      cross_route: 'cross_route_click'
    }[moduleType];
    const eventName = target.dataset.avWorldAction || inferredEvent;
    if (WORLD_EVENTS.has(eventName)) emit(eventName, target);
    if (target.closest('[data-au-track-action]')) {
      emit('au_track_interaction', target, { action: target.closest('[data-au-track-action]').dataset.auTrackAction || 'world_portal_click' });
    }
    if (target.closest('[data-av-v112-world-sequence]')) {
      emitV112('australia:world_cta_click', target, {}, 800);
    }
  });
  document.addEventListener('focusin', (event) => {
    activateWorld(event.target);
    keepCollectionChipVisible(event.target);
  });
  document.addEventListener('pointerover', (event) => {
    if (finePointer.matches) activateWorld(event.target);
  }, { passive: true });
  document.addEventListener('visitorConsentCollected', () => {
    document.querySelectorAll('[data-av-v112-world-sequence] [data-au-day-chapter]').forEach((node) => {
      const bounds = node.getBoundingClientRect();
      if (bounds.bottom > 0 && bounds.top < innerHeight) emitWorldEnter(node);
    });
  });
  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:section:unload', (event) => teardown(event.target));
  document.addEventListener('shopify:block:select', (event) => init(event.target));
  document.addEventListener('shopify:block:deselect', (event) => init(event.target));
  document.addEventListener('visibilitychange', () => { if (!document.hidden) init(document); });
  reducedMotion.addEventListener?.('change', () => init(document));
  reducedMotion.addListener?.(() => init(document));

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(document), { once: true });
  else init(document);
})();
